import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useEngine } from '../../state/engineStore';
import type { TrendSeries } from '../../engine/core/trends';
import { fmtSimTime } from '../shell/format';

export const PEN_COLOURS = [
  '#6fcf7d',
  '#7fb2d8',
  '#f2b45a',
  '#c88bd6',
  '#e97b7b',
  '#63d3c4',
  '#d9d36a',
  '#9aa3ff',
];

export interface Pen {
  tag: string;
  colour: string;
  /** Per-pen scale; null = autoscale from the tag's engineering range. */
  lo: number | null;
  hi: number | null;
  visible: boolean;
}

interface Props {
  pens: Pen[];
  windowS: number;
  /** Seconds before "now" that the right edge sits at; 0 = live. */
  offsetS: number;
  onPan(deltaS: number): void;
  onZoom(factor: number): void;
  height: number;
}

export function TrendChart({ pens, windowS, offsetS, onPan, onZoom, height }: Props): JSX.Element {
  const canvas = useRef<HTMLCanvasElement>(null);
  const seriesRef = useRef<TrendSeries[]>([]);
  const tick = useEngine((s) => s.tick);
  const simTime = useEngine((s) => s.snap?.simTime ?? 0);
  const meta = useEngine((s) => s.meta);
  const indexOf = useEngine((s) => s.indexOf);
  const requestTrend = useEngine((s) => s.requestTrend);
  const [cursor, setCursor] = useState<{ x: number; t: number } | null>(null);
  const pending = useRef(false);
  const drag = useRef<{ x: number; t0: number } | null>(null);

  const visible = useMemo(() => pens.filter((p) => p.visible), [pens]);
  const to = Math.max(0, simTime - offsetS);
  const from = Math.max(0, to - windowS);
  const key = visible.map((p) => p.tag).join(',');

  useEffect(() => {
    if (pending.current) return;
    pending.current = true;
    void requestTrend(visible.map((p) => p.tag), from, to, 900).then((s) => {
      seriesRef.current = s;
      pending.current = false;
      draw();
    });
  }, [tick >> 1, key, windowS, offsetS]); // eslint-disable-line react-hooks/exhaustive-deps

  const rangeFor = useCallback(
    (pen: Pen, s: TrendSeries | undefined): [number, number] => {
      if (pen.lo !== null && pen.hi !== null) return [pen.lo, pen.hi];
      const i = indexOf.get(pen.tag);
      const m = i === undefined ? undefined : meta[i];
      if (m) return [m.rangeLo, m.rangeHi];
      if (!s || s.mean.length === 0) return [0, 100];
      let lo = Infinity;
      let hi = -Infinity;
      for (let k = 0; k < s.mean.length; k++) {
        lo = Math.min(lo, s.min[k] as number);
        hi = Math.max(hi, s.max[k] as number);
      }
      const pad = Math.max((hi - lo) * 0.1, 1e-6);
      return [lo - pad, hi + pad];
    },
    [indexOf, meta],
  );

  function draw(): void {
    const cv = canvas.current;
    if (!cv) return;
    const dpr = window.devicePixelRatio || 1;
    const w = cv.clientWidth;
    const h = cv.clientHeight;
    if (cv.width !== w * dpr || cv.height !== h * dpr) {
      cv.width = w * dpr;
      cv.height = h * dpr;
    }
    const g = cv.getContext('2d');
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    const css = getComputedStyle(document.documentElement);
    g.fillStyle = css.getPropertyValue('--bg-deep') || '#1b1e21';
    g.fillRect(0, 0, w, h);

    const padL = 58;
    const padR = 12;
    const padT = 10;
    const padB = 22;
    const plotW = w - padL - padR;
    const plotH = h - padT - padB;
    const X = (t: number): number => padL + ((t - from) / Math.max(to - from, 1e-6)) * plotW;

    // Grid
    g.strokeStyle = css.getPropertyValue('--line-dim') || '#555';
    g.globalAlpha = 0.3;
    g.lineWidth = 1;
    g.beginPath();
    for (let i = 0; i <= 4; i++) {
      const y = padT + (plotH * i) / 4;
      g.moveTo(padL, y);
      g.lineTo(w - padR, y);
    }
    const ticks = 6;
    for (let i = 0; i <= ticks; i++) {
      const x = padL + (plotW * i) / ticks;
      g.moveTo(x, padT);
      g.lineTo(x, padT + plotH);
    }
    g.stroke();
    g.globalAlpha = 1;

    // Time axis labels
    g.fillStyle = css.getPropertyValue('--text-mute') || '#888';
    g.font = '10px ui-monospace, monospace';
    g.textAlign = 'center';
    for (let i = 0; i <= ticks; i++) {
      const t = from + ((to - from) * i) / ticks;
      g.fillText(fmtSimTime(t), padL + (plotW * i) / ticks, h - 7);
    }

    // Pens
    visible.forEach((pen, pi) => {
      const s = seriesRef.current[pi];
      if (!s) return;
      const [lo, hi] = rangeFor(pen, s);
      const Y = (v: number): number => padT + plotH - ((v - lo) / Math.max(hi - lo, 1e-9)) * plotH;

      if (s.periodS > 1) {
        g.fillStyle = pen.colour;
        g.globalAlpha = 0.16;
        g.beginPath();
        for (let k = 0; k < s.t.length; k++) g.lineTo(X(s.t[k] as number), Y(s.max[k] as number));
        for (let k = s.t.length - 1; k >= 0; k--) g.lineTo(X(s.t[k] as number), Y(s.min[k] as number));
        g.closePath();
        g.fill();
        g.globalAlpha = 1;
      }
      g.strokeStyle = pen.colour;
      g.lineWidth = 1.5;
      g.beginPath();
      for (let k = 0; k < s.t.length; k++) {
        const px = X(s.t[k] as number);
        const py = Y(s.mean[k] as number);
        if (k === 0) g.moveTo(px, py);
        else g.lineTo(px, py);
      }
      g.stroke();

      // Left axis for the first pen only, to keep the chart readable.
      if (pi === 0) {
        g.fillStyle = pen.colour;
        g.textAlign = 'right';
        for (let i = 0; i <= 4; i++) {
          const v = hi - ((hi - lo) * i) / 4;
          g.fillText(v.toPrecision(4), padL - 6, padT + (plotH * i) / 4 + 3);
        }
      }
    });

    // Cursor
    if (cursor) {
      g.strokeStyle = css.getPropertyValue('--text-dim') || '#ccc';
      g.globalAlpha = 0.7;
      g.setLineDash([3, 3]);
      g.beginPath();
      g.moveTo(cursor.x, padT);
      g.lineTo(cursor.x, padT + plotH);
      g.stroke();
      g.setLineDash([]);
      g.globalAlpha = 1;
    }

    // Resolution note — the operator should always know what they are looking at.
    const s0 = seriesRef.current[0];
    if (s0) {
      g.fillStyle = css.getPropertyValue('--text-mute') || '#888';
      g.textAlign = 'left';
      g.fillText(`${s0.tier} tier · ${s0.periodS} s/sample`, padL + 4, padT + 11);
    }
  }

  useEffect(draw);

  const readout = useMemo(() => {
    if (!cursor) return null;
    return visible.map((pen, i) => {
      const s = seriesRef.current[i];
      if (!s || s.t.length === 0) return { tag: pen.tag, colour: pen.colour, value: NaN };
      let best = 0;
      let bestD = Infinity;
      for (let k = 0; k < s.t.length; k++) {
        const d = Math.abs((s.t[k] as number) - cursor.t);
        if (d < bestD) {
          bestD = d;
          best = k;
        }
      }
      return { tag: pen.tag, colour: pen.colour, value: s.mean[best] as number };
    });
  }, [cursor, visible]);

  return (
    <div style={{ position: 'relative', height: height > 0 ? height : '100%' }}>
      <canvas
        ref={canvas}
        style={{
          width: '100%',
          height: height > 0 ? height : '100%',
          display: 'block',
          cursor: drag.current ? 'grabbing' : 'crosshair',
        }}
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - r.left;
          const padL = 58;
          const plotW = r.width - padL - 12;
          const t = from + ((x - padL) / Math.max(plotW, 1)) * (to - from);
          setCursor({ x, t });
          if (drag.current) {
            const dt = ((drag.current.x - x) / Math.max(plotW, 1)) * (to - from);
            onPan(dt);
            drag.current = { x, t0: t };
          }
        }}
        onMouseLeave={() => {
          setCursor(null);
          drag.current = null;
        }}
        onMouseDown={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          drag.current = { x: e.clientX - r.left, t0: 0 };
        }}
        onMouseUp={() => {
          drag.current = null;
        }}
        onWheel={(e) => {
          onZoom(e.deltaY > 0 ? 1.25 : 0.8);
        }}
      />
      {readout && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            right: 18,
            background: 'var(--panel)',
            border: '1px solid var(--line-dim)',
            borderRadius: 3,
            padding: '5px 8px',
            fontSize: 11,
            pointerEvents: 'none',
          }}
        >
          <div className="muted num" style={{ marginBottom: 3 }}>
            {fmtSimTime(cursor?.t ?? 0)}
          </div>
          {readout.map((r) => (
            <div key={r.tag} className="num" style={{ color: r.colour, whiteSpace: 'nowrap' }}>
              {r.tag} {Number.isFinite(r.value) ? r.value.toPrecision(5) : '——'}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
